import { api } from './api';

export type TicketFolderStatus = 'OPEN' | 'CLOSED';

export interface FolderSummary {
  id: string;
  ownerUserId: string;
  ownerName: string;
  name: string;
  description: string | null;
  status: TicketFolderStatus;
  tripStartDate: string | null;
  tripEndDate: string | null;
  ticketCount: number;
  totalCents: number;
  closedAt: string | null;
  closedByName: string | null;
  createdAt: string;
}

export interface TicketSummary {
  id: string;
  folderId: string;
  ownerUserId: string;
  category: string | null;
  title: string;
  ticketDate: string | null;
  amountCents: number | null;
  currency: string;
  notes: string | null;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  createdAt: string;
}

/** Catálogo cerrado de categorías que el desplegable ofrece al empleado. */
export const TICKET_CATEGORIES = [
  'Comida',
  'Gasolina',
  'Alojamiento',
  'Viaje',
  'Transporte',
  'Aparcamiento',
  'Material',
  'Otros',
] as const;
export type TicketCategory = (typeof TICKET_CATEGORIES)[number];

export interface FolderDetail extends FolderSummary {
  tickets: TicketSummary[];
}

// ----------------- Viajes (RRHH) -----------------

export type TripStatus = 'OPEN' | 'CLOSED';

/** Una carpeta (empleado) dentro de un viaje, como la ve RRHH. */
export interface TripFolderRow {
  folderId: string;
  ownerUserId: string;
  ownerName: string;
  status: TripStatus;
  ticketCount: number;
  totalCents: number;
}

export interface TripSummary {
  id: string;
  name: string;
  description: string | null;
  status: TripStatus;
  tripStartDate: string | null;
  tripEndDate: string | null;
  employeeCount: number;
  ticketCount: number;
  totalCents: number;
  createdByName: string;
  closedAt: string | null;
  closedByName: string | null;
  createdAt: string;
}

export interface TripDetail extends TripSummary {
  folders: TripFolderRow[];
}

// ----------------- Empleado -----------------

export const listMyFolders = (status?: TicketFolderStatus) =>
  api<FolderSummary[]>(`/tickets/folders${status ? `?status=${status}` : ''}`);

export const getFolder = (id: string) => api<FolderDetail>(`/tickets/folders/${id}`);

/**
 * ¿El empleado tiene alguna carpeta abierta de un viaje activo? Controla
 * la visibilidad del menú "Tickets viaje". El empleado ya NO crea/cierra/
 * borra carpetas: las gestiona RRHH a través de los viajes.
 */
export const ticketsVisibility = () =>
  api<{ hasOpenFolders: boolean }>('/tickets/visibility');

export const uploadTicket = async (
  folderId: string,
  args: {
    category?: string;
    title: string;
    ticketDate?: string;
    amountCents?: number;
    currency?: string;
    notes?: string;
    ocrText?: string;
    file: File;
  },
): Promise<TicketSummary> => {
  const form = new FormData();
  form.append('file', args.file);
  if (args.category) form.append('category', args.category);
  form.append('title', args.title);
  if (args.ticketDate) form.append('ticketDate', args.ticketDate);
  if (args.amountCents != null) form.append('amountCents', String(args.amountCents));
  if (args.currency) form.append('currency', args.currency);
  if (args.notes) form.append('notes', args.notes);
  if (args.ocrText) form.append('ocrText', args.ocrText);
  // Para multipart no pasamos Content-Type — el navegador lo añade con boundary.
  const res = await fetch(`/api/v1/tickets/folders/${folderId}/tickets`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as Record<string, unknown>);
    const message =
      (Array.isArray(body.message) ? body.message[0] : body.message) ?? `HTTP ${res.status}`;
    throw new Error(message as string);
  }
  return (await res.json()) as TicketSummary;
};

export const updateTicket = (
  ticketId: string,
  input: {
    category?: string;
    title?: string;
    ticketDate?: string;
    amountCents?: number;
    currency?: string;
    notes?: string;
  },
) =>
  api<TicketSummary>(`/tickets/${ticketId}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });

export const deleteTicket = (ticketId: string) =>
  api<void>(`/tickets/${ticketId}`, { method: 'DELETE' });

export const ticketDownloadUrl = (ticketId: string) =>
  `/api/v1/tickets/${ticketId}/download`;

// ----------------- Viajes: gestión RRHH -----------------

export const listTrips = (status?: TripStatus) =>
  api<TripSummary[]>(`/admin/trips${status ? `?status=${status}` : ''}`);

export const getTrip = (id: string) => api<TripDetail>(`/admin/trips/${id}`);

export const createTrip = (input: {
  name: string;
  description?: string;
  tripStartDate?: string;
  tripEndDate?: string;
  employeeUserIds: string[];
}) =>
  api<TripSummary>('/admin/trips', {
    method: 'POST',
    body: JSON.stringify(input),
  });

export const updateTrip = (
  id: string,
  input: {
    name?: string;
    description?: string;
    tripStartDate?: string;
    tripEndDate?: string;
    /** Set COMPLETO de empleados deseado. Si se omite, no se tocan. */
    employeeUserIds?: string[];
  },
) =>
  api<TripSummary>(`/admin/trips/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });

export const closeTrip = (id: string) =>
  api<TripSummary>(`/admin/trips/${id}/close`, { method: 'POST' });

export const reopenTrip = (id: string) =>
  api<TripSummary>(`/admin/trips/${id}/reopen`, { method: 'POST' });

export const deleteTrip = (id: string) =>
  api<void>(`/admin/trips/${id}/delete`, { method: 'POST' });

// ----------------- Carpeta individual (RRHH ve la de cada empleado) -----------------

export const getAdminFolder = (id: string) =>
  api<FolderDetail>(`/admin/tickets/folders/${id}`);

export const reopenFolder = (id: string) =>
  api<FolderSummary>(`/admin/tickets/folders/${id}/reopen`, { method: 'POST' });

export const adminDeleteFolder = (id: string) =>
  api<void>(`/admin/tickets/folders/${id}/delete`, { method: 'POST' });

export const folderPdfUrl = (id: string) => `/api/v1/admin/tickets/folders/${id}/pdf`;
export const folderXlsxUrl = (id: string) => `/api/v1/admin/tickets/folders/${id}/xlsx`;

// ----------------- Utils -----------------

export const formatAmount = (cents: number | null | undefined, currency = 'EUR') => {
  if (cents == null) return '—';
  return `${(cents / 100).toFixed(2)} ${currency}`;
};
